import { DEVICE_SIZES, buildImageUrl, buildSrcSet } from "../../utils/imageUrl"

/**
 * Renders a listing photo through the `/img/*` CDN behaviour.
 *
 * The real URL and srcset are emitted immediately, including during SSR. There
 * used to be a swap here — non-eager images rendered `fallbackSrc` (a skeleton
 * placeholder) first and only switched to the real URL after the placeholder's
 * onLoad fired. That meant 35 of 36 listing cards shipped
 * `/images/static/gun-skele*.webp` in the server-rendered HTML, so non-rendering
 * crawlers and link unfurlers saw placeholders, every card cost two requests, and
 * the real photo started downloading a paint late. It also produced a hydration
 * mismatch, because the placeholder is chosen with Math.random() and so differed
 * between server and client.
 *
 * `fallbackSrc` is now purely an error path: it is used if `src` is missing or
 * not rewritable, and swapped in on a failed load.
 */
export const Image = ({
    src,
    alt,
    sizes,
    widths = DEVICE_SIZES,
    quality = 70,
    fallbackWidth = 1200,
    width,
    height,
    eager = false,
    priority,
    fallbackSrc,
    className,
    style,
    onClick,
    onError,
    onLoad,
    ...rest
}) => {
    const realUrl = buildImageUrl(src, { width: fallbackWidth, quality })
    const realSrcSet = buildSrcSet(src, widths, quality)

    // buildImageUrl returns "" for a missing or unusable src; never emit src="",
    // which browsers resolve against the current document.
    const resolvedSrc = realUrl || fallbackSrc

    // On a failed load revert the DOM node to the fallback. srcset must be
    // cleared too — browsers select from srcset when it is present and would
    // otherwise re-fire error on the already-failed candidate. Direct DOM
    // mutation is safe because no state changes after this, so React will not
    // overwrite it.
    const handleError = (e) => {
        // Resolve the fallback the way the DOM does before comparing. Reading
        // `.src` back always yields an absolute URL
        // ("http://host/images/static/x.webp") while `fallbackSrc` is
        // root-relative ("/images/static/x.webp"), so the previous
        // `.src !== fallbackSrc` check never matched: the fallback was
        // re-applied on every error, and because the swap re-fires error when
        // the fallback also fails, that looped forever.
        //
        // Comparing resolved URLs — rather than latching a "already swapped"
        // flag on the node — keeps the swap idempotent while still re-applying
        // if a re-render restores a broken `src`, so a failed pipeline stays a
        // placeholder instead of degrading to a broken-image icon.
        if (fallbackSrc) {
            const resolvedFallback = new URL(
                fallbackSrc,
                e.currentTarget.baseURI
            ).href

            if (e.currentTarget.src !== resolvedFallback) {
                e.currentTarget.srcset = ""
                e.currentTarget.src = fallbackSrc
            }
        }
        onError?.(e)
    }

    return (
        <img
            src={resolvedSrc}
            srcSet={realSrcSet}
            sizes={realSrcSet ? sizes : undefined}
            alt={alt}
            width={width}
            height={height}
            loading={eager ? "eager" : "lazy"}
            fetchPriority={priority}
            decoding="async"
            className={className}
            style={style}
            onClick={onClick}
            onError={handleError}
            onLoad={onLoad}
            {...rest}
        />
    )
}
